import { isDeletedTimelineEntry } from '../../services/timeline/deletionRules';
import { canViewerSeeCareRecord } from '../../services/timeline/recordVisibility';
import { isTimelineVisibleSourceEntry } from '../../services/timeline/timelineSourceVisibility';
import { getEntryDate, getEntryId, getObservation } from '../../components/Timeline/utils/medicationTimelinePatternObservationUtils';
import { selectMedicationChangeTimelinePatterns } from '../../components/Timeline/utils/medicationTimelinePatternDetector';

const DAY_MS = 24 * 60 * 60 * 1000;
const RECENT_DAYS = 30;
const CO_OCCURRENCE_DAYS = 2;
const MIN_REPEATED_OCCURRENCES = 3;
const MIN_STRATEGY_OCCURRENCES = 4;
const MAX_PATTERNS = 3;

const CATEGORY_LABELS = {
  behavior: 'Behavior observations',
  sleep: 'Sleep observations',
  food: 'Food and appetite observations',
  mood: 'Mood observations',
};

const STRATEGIES = [
  { key: 'visual-schedule', label: 'A visual schedule', pattern: /\bvisual schedule\b/i },
  { key: 'break', label: 'A break', pattern: /\b(?:sensory )?break\b/i },
  { key: 'deep-breathing', label: 'Deep breathing', pattern: /\b(?:deep breath(?:ing|s)?|breathing exercise)\b/i },
  { key: 'timer', label: 'A timer', pattern: /\btimer\b/i },
  { key: 'first-then', label: 'A first-then support', pattern: /\bfirst[ -]then\b/i },
  { key: 'choice', label: 'Offering a choice', pattern: /\b(?:offered|offering|gave|giving) (?:a |him |her |them )?choice\b/i },
];

const POSITIVE_OUTCOME_PATTERN = /\b(easier|successful|successfully|calm(?:ed)?|settled|helped|worked well|smooth(?:ly)?|better|regulated|recovered)\b/i;
const AGGRESSION_PATTERN = /\b(aggress(?:ion|ive|ively)?|hit(?:ting)?|kick(?:ing)?|bit(?:ing|ten)?|scratch(?:ing|ed)?|push(?:ing|ed)?|throwing objects?)\b/i;
const POOR_SLEEP_PATTERN = /\b(poor|bad|restless|rough|broken|woke|waking|awake|insomnia|little sleep|didn['’]?t sleep|no sleep)\b/i;

const normalizeText = (value = '') => String(value ?? '').replace(/\s+/g, ' ').trim();

const getEntryText = (entry = {}) => [
  entry.title,
  entry.displayTitle,
  entry.content,
  entry.notes,
  entry.note,
  entry.description,
  entry.summary,
  entry.rawText,
  entry.triggerSummary,
  entry.triggerNotes,
  entry.remedy,
  entry.outcome,
  entry.incidentData?.triggerSummary,
  entry.incidentData?.triggerNotes,
  entry.incidentData?.remedy,
  entry.sleepDetails?.quality,
  entry.sleepDetails?.disturbances,
].map(normalizeText).filter(Boolean).join(' ');

const getEntryKind = (entry = {}) => {
  const observationKind = getObservation(entry)?.kind;
  if (observationKind) return observationKind;

  const values = [entry.category, entry.type, entry.timelineType, entry.logCategory, entry.incidentCategoryId]
    .map((value) => normalizeText(value).toLowerCase());
  if (values.includes('behavior') || values.includes('incident')) return 'behavior';
  if (values.includes('sleep')) return 'sleep';
  if (values.includes('food') || values.includes('meal')) return 'food';
  if (values.includes('mood')) return 'mood';
  return '';
};

const isArchivedOrHidden = (entry = {}) => (
  entry.archived === true
  || entry.isArchived === true
  || entry.hiddenFromTimeline === true
  || ['archived', 'discarded'].includes(normalizeText(entry.status).toLowerCase())
  || ['discarded'].includes(normalizeText(entry.reviewState || entry.reviewStatus).toLowerCase())
);

export const getPatternEligibleEntries = (entries = [], viewerRole = null) => (
  (Array.isArray(entries) ? entries : [])
    .filter((entry) => entry && typeof entry === 'object')
    .filter((entry) => getEntryId(entry) && getEntryDate(entry))
    .filter((entry) => !isDeletedTimelineEntry(entry) && !isArchivedOrHidden(entry))
    .filter(isTimelineVisibleSourceEntry)
    .filter((entry) => !viewerRole || canViewerSeeCareRecord({ viewerRole, record: entry }))
);

const uniqueIds = (entries = []) => [...new Set(entries.map(getEntryId).filter(Boolean))];
const formatCount = (count) => `${count} ${count === 1 ? 'time' : 'times'}`;

const formatRange = (start, end) => `${start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}–${end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}`;

const buildPattern = ({ id, type, title, finding, entries, start, end, disclaimer = '', confidence = 'Moderate evidence' }) => ({
  id,
  type,
  title,
  finding,
  dateRange: { start: start.toISOString(), end: end.toISOString(), label: formatRange(start, end) },
  supportingRecordCount: uniqueIds(entries).length,
  evidenceEntryIds: uniqueIds(entries),
  disclaimer,
  confidence,
});

const detectFrequencyPatterns = (entries, boundaries) => Object.keys(CATEGORY_LABELS).flatMap((kind) => {
  const matching = entries.filter((entry) => getEntryKind(entry) === kind);
  const recent = matching.filter((entry) => getEntryDate(entry) >= boundaries.recentStart && getEntryDate(entry) <= boundaries.now);
  const previous = matching.filter((entry) => getEntryDate(entry) >= boundaries.previousStart && getEntryDate(entry) < boundaries.recentStart);
  const difference = recent.length - previous.length;
  const enoughEvidence = Math.max(recent.length, previous.length) >= MIN_REPEATED_OCCURRENCES;
  if (!enoughEvidence || Math.abs(difference) < 2 || (recent.length && previous.length && Math.max(recent.length, previous.length) / Math.min(recent.length, previous.length) < 1.5)) return [];

  const direction = difference > 0 ? 'increased' : 'decreased';
  return [buildPattern({
    id: `frequency-${kind}`,
    type: 'frequency_change',
    title: `${CATEGORY_LABELS[kind]} ${direction}`,
    finding: `${CATEGORY_LABELS[kind]} were recorded ${formatCount(recent.length)} in the last 30 days compared with ${formatCount(previous.length)} in the previous 30 days.`,
    entries: [...recent, ...previous],
    start: boundaries.previousStart,
    end: boundaries.now,
  })];
});

const isPoorSleepEntry = (entry) => {
  const observation = getObservation(entry);
  const quality = normalizeText(observation?.quality || entry.sleepDetails?.quality).toLowerCase();
  const duration = Number(observation?.durationMinutes);
  return POOR_SLEEP_PATTERN.test(`${quality} ${getEntryText(entry)}`) || (Number.isFinite(duration) && duration > 0 && duration < 360);
};

const detectSleepBehaviorPattern = (entries, boundaries) => {
  const recentEntries = entries.filter((entry) => getEntryDate(entry) >= boundaries.recentStart && getEntryDate(entry) <= boundaries.now);
  const behaviorEntries = recentEntries.filter((entry) => getEntryKind(entry) === 'behavior');
  const poorSleepEntries = recentEntries.filter((entry) => getEntryKind(entry) === 'sleep' && isPoorSleepEntry(entry));
  if (behaviorEntries.length < MIN_REPEATED_OCCURRENCES || poorSleepEntries.length < 2) return null;

  const nearbyBehavior = behaviorEntries.filter((behaviorEntry) => poorSleepEntries.some((sleepEntry) => (
    Math.abs(getEntryDate(behaviorEntry).getTime() - getEntryDate(sleepEntry).getTime()) <= CO_OCCURRENCE_DAYS * DAY_MS
  )));
  if (nearbyBehavior.length < MIN_REPEATED_OCCURRENCES || nearbyBehavior.length / behaviorEntries.length < 0.5) return null;

  const supportingSleep = poorSleepEntries.filter((sleepEntry) => nearbyBehavior.some((behaviorEntry) => (
    Math.abs(getEntryDate(behaviorEntry).getTime() - getEntryDate(sleepEntry).getTime()) <= CO_OCCURRENCE_DAYS * DAY_MS
  )));
  return buildPattern({
    id: 'cooccurrence-sleep-behavior',
    type: 'repeated_cooccurrence',
    title: 'Possible sleep connection',
    finding: `${behaviorEntries.length} behavior observations were recorded in the last 30 days. Poor sleep was documented near ${nearbyBehavior.length} of them.`,
    entries: [...nearbyBehavior, ...supportingSleep],
    start: boundaries.recentStart,
    end: boundaries.now,
    disclaimer: 'These events were documented around the same time. Lifelog does not know whether one caused the other.',
  });
};

const detectStrategyPattern = (entries, boundaries) => {
  const recentEntries = entries.filter((entry) => getEntryDate(entry) >= boundaries.previousStart && getEntryDate(entry) <= boundaries.now);
  for (const strategy of STRATEGIES) {
    const mentioned = recentEntries.filter((entry) => strategy.pattern.test(getEntryText(entry)));
    const positive = mentioned.filter((entry) => POSITIVE_OUTCOME_PATTERN.test(getEntryText(entry)));
    if (mentioned.length >= MIN_STRATEGY_OCCURRENCES && positive.length >= 3 && positive.length / mentioned.length >= 0.6) {
      return buildPattern({
        id: `strategy-${strategy.key}`,
        type: 'what_seems_to_help',
        title: 'What seems to help',
        finding: `${strategy.label} appeared in ${mentioned.length} observations. ${positive.length} described a better, easier, or successful outcome.`,
        entries: mentioned,
        start: boundaries.previousStart,
        end: boundaries.now,
        disclaimer: 'This is a repeated association in saved notes, not proof that the strategy caused the outcome.',
        confidence: 'Stronger evidence',
      });
    }
  }
  return null;
};

const detectNotableChange = (entries, boundaries) => {
  const aggressionEntries = entries.filter((entry) => AGGRESSION_PATTERN.test(getEntryText(entry)));
  const recent = aggressionEntries.filter((entry) => getEntryDate(entry) >= boundaries.recentStart && getEntryDate(entry) <= boundaries.now);
  const previous = aggressionEntries.filter((entry) => getEntryDate(entry) >= boundaries.previousStart && getEntryDate(entry) < boundaries.recentStart);
  if (recent.length < MIN_REPEATED_OCCURRENCES || previous.length > 1 || recent.length - previous.length < 3) return null;
  return buildPattern({
    id: 'notable-aggression',
    type: 'new_notable_change',
    title: 'Recent change',
    finding: `Aggression-related observations appeared ${recent.length} times in the last 30 days compared with ${previous.length} time${previous.length === 1 ? '' : 's'} in the previous 30 days.`,
    entries: [...recent, ...previous],
    start: boundaries.previousStart,
    end: boundaries.now,
    disclaimer: 'This may be worth watching. Lifelog is describing what was recorded, not making a diagnosis.',
  });
};

const mapMedicationPatterns = (entries) => selectMedicationChangeTimelinePatterns(entries).slice(0, 1).map((pattern) => {
  const evidenceIds = [
    pattern.medicationChange?.entryId,
    ...(pattern.evidence?.before || []).map((item) => item.entryId),
    ...(pattern.evidence?.after || []).map((item) => item.entryId),
  ].filter(Boolean);
  const evidenceEntries = entries.filter((entry) => evidenceIds.includes(getEntryId(entry)));
  const start = new Date(Math.min(...evidenceEntries.map((entry) => getEntryDate(entry).getTime())));
  const end = new Date(Math.max(...evidenceEntries.map((entry) => getEntryDate(entry).getTime())));
  return {
    ...buildPattern({
    id: `insight-${pattern.id}`,
    type: 'medication_timing',
    title: 'Possible medication timing pattern',
    finding: `${CATEGORY_LABELS[pattern.patternCategory] || 'Care observations'} changed around a recorded medication change.`,
    entries: evidenceEntries,
    start,
    end,
    disclaimer: 'This is a timing pattern, not proof that the medication caused the change. Do not change medication without guidance from the prescribing clinician.',
    }),
    medicationChangeAt: pattern.medicationChange?.effectiveAt || '',
  };
});

export const detectCarePatterns = ({ entries = [], viewerRole = null, now = new Date(), limit = MAX_PATTERNS } = {}) => {
  const safeNow = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
  const boundaries = {
    now: safeNow,
    recentStart: new Date(safeNow.getTime() - RECENT_DAYS * DAY_MS),
    previousStart: new Date(safeNow.getTime() - RECENT_DAYS * 2 * DAY_MS),
  };
  const eligibleEntries = getPatternEligibleEntries(entries, viewerRole);
  const notable = detectNotableChange(eligibleEntries, boundaries);
  const frequency = detectFrequencyPatterns(eligibleEntries, boundaries)
    .filter((pattern) => !(notable && pattern.id === 'frequency-behavior'));
  const candidates = [
    ...mapMedicationPatterns(eligibleEntries),
    notable,
    detectSleepBehaviorPattern(eligibleEntries, boundaries),
    detectStrategyPattern(eligibleEntries, boundaries),
    ...frequency,
  ].filter(Boolean);

  return candidates.slice(0, Math.max(0, Number(limit) || MAX_PATTERNS));
};

export const selectHomeCareInsight = (patterns = [], now = new Date()) => {
  const medicationPriorityStart = new Date(now.getTime() - RECENT_DAYS * DAY_MS);
  const priorities = {
    new_notable_change: 1,
    what_seems_to_help: 2,
    repeated_cooccurrence: 3,
    frequency_change: 4,
  };

  return [...patterns]
    .filter((pattern) => pattern.type !== 'medication_timing' || (
      pattern.medicationChangeAt
      && new Date(pattern.medicationChangeAt) >= medicationPriorityStart
      && new Date(pattern.medicationChangeAt) <= now
    ))
    .sort((left, right) => {
      const leftPriority = left.type === 'medication_timing' ? 0 : priorities[left.type] ?? 99;
      const rightPriority = right.type === 'medication_timing' ? 0 : priorities[right.type] ?? 99;
      return leftPriority - rightPriority || right.supportingRecordCount - left.supportingRecordCount;
    })[0] || null;
};

export const CARE_PATTERN_RULES = {
  recentDays: RECENT_DAYS,
  previousDays: RECENT_DAYS,
  coOccurrenceDays: CO_OCCURRENCE_DAYS,
  minimumRepeatedOccurrences: MIN_REPEATED_OCCURRENCES,
  minimumStrategyOccurrences: MIN_STRATEGY_OCCURRENCES,
  maximumPatterns: MAX_PATTERNS,
};

export default detectCarePatterns;
